import {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
} from 'react'
import Attendance from '@/modules/Attendance/classes/Attendance'
import { useHistory } from 'react-router-dom'
import { useForceUpdate } from '@react-spring/shared'
import { setCache } from '@/utils/cache'
import { FunctionComponentChildren } from '@/@types/react'

type updateAttendanceFn = (RA: Attendance) => Attendance

export interface IAttendanceContext {
  attendance: Attendance
  setAttendance(call: updateAttendanceFn): void
  redirectIfDontHaveAttendance(): void
  resetAttendance(): void
}

const AttendanceContext = createContext<IAttendanceContext>(null)

export const AttendanceProvider: FunctionComponentChildren = ({ children }) => {
  const [attendanceState, setAttendanceState] = useState<Attendance>(
    () => new Attendance(),
  )
  const history = useHistory()
  const forceUpdate = useForceUpdate()

  const redirectIfDontHaveAttendance = useCallback(() => {
    if (attendanceState?.speciality) return
    history.push('/')
  }, [attendanceState?.speciality, history])

  const setAttendance = useCallback(
    (call: updateAttendanceFn) => {
      const newAttendance = call(attendanceState)
      setAttendanceState(newAttendance)
      setCache('attendance', attendanceState)
      forceUpdate()
    },
    [attendanceState, forceUpdate],
  )
  const resetAttendance = useCallback(() => {
    setAttendanceState(new Attendance())
  }, [])

  const attendanceContextValue = useMemo(
    () => ({
      attendance: attendanceState,
      setAttendance,
      redirectIfDontHaveAttendance,
      resetAttendance,
    }),
    [
      attendanceState,
      setAttendance,
      redirectIfDontHaveAttendance,
      resetAttendance,
    ],
  )

  return (
    <AttendanceContext.Provider value={attendanceContextValue}>
      {children}
    </AttendanceContext.Provider>
  )
}

export const useAttendanceContext = (): IAttendanceContext => {
  const context = useContext(AttendanceContext)
  if (!context) {
    throw new Error('useAuthContext must be used within a AttendanceProvider')
  }
  return context
}
